import React, { useState, useEffect, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import {
  IconTrendingUp,
  IconTrendingDown,
  IconPlus,
  IconTrash,
  IconInfoCircle,
} from "@tabler/icons-react";
import { message, Tooltip } from "antd";
import { useCustomer } from "_contexts/useCustomer";
import { getBudget, saveBudget } from "_graphql/queries/portal";
import {
  cashCategories,
  assetCategories,
} from "_helpers/budget";
import AddItemModal from "./_components/AddItemModal/AddItemModal";
import "./BudgetCalculator.scss";

function BudgetCalculator() {
  const client = useApolloClient();
  const { applicationId, loading: customerLoading } = useCustomer();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [budgetItems, setBudgetItems] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  // Load budget data
  const loadBudget = async () => {
    if (!applicationId) return;

    try {
      const response = await client.query({
        query: getBudget,
        variables: { application_id: applicationId },
        fetchPolicy: "network-only",
      });
      const items = response?.data?.getBudget?.items || [];
      setBudgetItems(items);
    } catch (err) {
      console.error("Error loading budget:", err);
    }
    setLoading(false);
  };

  // Save budget data
  const updateBudget = async (newItems) => {
    setSaving(true);
    try {
      await client.mutate({
        mutation: saveBudget,
        variables: {
          application_id: applicationId,
          items: newItems.map((item) => ({
            name: item.name,
            category: item.category,
            type: item.type,
            value: item.value,
          })),
        },
      });
      setBudgetItems(newItems);
      message.success("Budget updated");
    } catch (err) {
      console.error("Error saving budget:", err);
      message.error("Error saving budget");
    }
    setSaving(false);
  };

  useEffect(() => {
    if (applicationId) {
      loadBudget();
    }
  }, [applicationId]);

  // Calculate financial metrics
  const metrics = useMemo(() => {
    const incomeItemsList = budgetItems.filter((b) => b.type === "income");
    const expenseItemsList = budgetItems.filter((b) => b.type === "expense");

    // Monthly income = income items that are in "Monthly Income" category
    const monthlyIncome = incomeItemsList
      .filter((b) => b.category === "Monthly Income")
      .reduce((sum, b) => sum + (b.value || 0), 0);

    // Monthly expenses = all expense items
    const monthlyExpenses = expenseItemsList.reduce(
      (sum, b) => sum + (b.value || 0),
      0
    );

    // Cash flow = monthly income - monthly expenses
    const cashFlow = monthlyIncome - monthlyExpenses;

    // Total cash = items in cash categories
    const totalCash = incomeItemsList
      .filter((b) => cashCategories.includes(b.category))
      .reduce((sum, b) => sum + (b.value || 0), 0);

    // Total assets = items in asset categories
    const totalAssets = incomeItemsList
      .filter((b) => assetCategories.includes(b.category))
      .reduce((sum, b) => sum + (b.value || 0), 0);

    return {
      monthlyIncome,
      monthlyExpenses,
      cashFlow,
      totalCash,
      totalAssets,
    };
  }, [budgetItems]);

  // Format currency
  const formatCurrency = (amount, showSign = false) => {
    const formatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount));

    if (showSign && amount !== 0) {
      return amount >= 0 ? `+${formatted}` : `-${formatted}`;
    }
    return formatted;
  };

  // Handle add items from modal
  const handleAddItems = (newItems) => {
    const existingNames = budgetItems.map((b) => b.name);
    const itemsToAdd = newItems.filter((item) => !existingNames.includes(item.name));
    const updatedItems = [...budgetItems, ...itemsToAdd];
    updateBudget(updatedItems);
    setModalOpen(false);
  };

  // Handle update item value
  const handleUpdateValue = (name, newValue) => {
    const updatedItems = budgetItems.map((item) =>
      item.name === name ? { ...item, value: newValue } : item
    );
    updateBudget(updatedItems);
  };

  // Handle delete item
  const handleDeleteItem = (name) => {
    const updatedItems = budgetItems.filter((item) => item.name !== name);
    updateBudget(updatedItems);
  };

  // Separate items by type
  const incomeItems = budgetItems.filter((b) => b.type === "income");
  const expenseItems = budgetItems.filter((b) => b.type === "expense");

  if (loading || customerLoading) {
    return (
      <div className="budget-calculator-page">
        <div className="budget-loading">
          <div className="loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="budget-calculator-page">
      {/* Header */}
      <div className="budget-header">
        <h1 className="budget-title">Financial Snapshot</h1>
        <p className="budget-description">
          Track your income & expenses and plan for the future by budgeting
          effectively.
        </p>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="budget-content">
        {/* Left Column - Income & Expenses Items */}
        <div className="items-card">
          {/* Income Section */}
          <div className="items-section">
            <div className="section-header-row">
              <div className="section-title-group">
                <div className="type-badge income">
                  <IconTrendingUp size={18} />
                </div>
                <span className="section-label">Income</span>
              </div>
              <button
                className="add-button"
                onClick={() => setModalOpen(true)}
                disabled={saving}
              >
                <IconPlus size={16} />
                <span>Add Income & Expenses</span>
              </button>
            </div>
            <div className="items-list">
              {incomeItems.map((item) => (
                <BudgetItemRow
                  key={item.name}
                  item={item}
                  type="income"
                  onSave={(value) => handleUpdateValue(item.name, value)}
                  onDelete={() => handleDeleteItem(item.name)}
                  formatCurrency={formatCurrency}
                />
              ))}
              {incomeItems.length === 0 && (
                <div className="empty-state">No income items added</div>
              )}
            </div>
          </div>

          {/* Dashed Divider */}
          <div className="section-divider" />

          {/* Expenses Section */}
          <div className="items-section">
            <div className="section-header-row">
              <div className="section-title-group">
                <div className="type-badge expense">
                  <IconTrendingDown size={18} />
                </div>
                <span className="section-label">Expenses</span>
                {expenseItems.length > 0 && (
                  <span className="item-count">({expenseItems.length})</span>
                )}
              </div>
            </div>
            <div className="items-list">
              {expenseItems.map((item) => (
                <BudgetItemRow
                  key={item.name}
                  item={item}
                  type="expense"
                  onSave={(value) => handleUpdateValue(item.name, value)}
                  onDelete={() => handleDeleteItem(item.name)}
                  formatCurrency={formatCurrency}
                />
              ))}
              {expenseItems.length === 0 && (
                <div className="empty-state">No expense items added</div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Summary Card */}
        <div className="summary-card">
          {/* Cashflow Hero */}
          <div className="cashflow-section">
            <div className="cashflow-label">
              <span>CASHFLOW</span>
              <Tooltip title="This how much money you have left after subtracting your expenses from your income each month.">
                <IconInfoCircle size={16} />
              </Tooltip>
            </div>
            <div className={`cashflow-value ${metrics.cashFlow >= 0 ? "positive" : "negative"}`}>
              {formatCurrency(metrics.cashFlow, false)}
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="metrics-grid">
            {/* Row 1 */}
            <div className="metrics-row">
              <div className="metric-item">
                <div className="metric-label">
                  <span>MONTHLY INCOME</span>
                  <Tooltip title="Your total monthly income from all sources">
                    <IconInfoCircle size={14} />
                  </Tooltip>
                </div>
                <div className="metric-value positive">
                  +{formatCurrency(metrics.monthlyIncome)}
                </div>
              </div>
              <div className="metric-divider" />
              <div className="metric-item">
                <div className="metric-label">
                  <span>MONTHLY EXPENSES</span>
                  <Tooltip title="Your total monthly expenses">
                    <IconInfoCircle size={14} />
                  </Tooltip>
                </div>
                <div className="metric-value negative">
                  -{formatCurrency(metrics.monthlyExpenses)}
                </div>
              </div>
            </div>

            <div className="metrics-row-divider" />

            {/* Row 2 */}
            <div className="metrics-row">
              <div className="metric-item">
                <div className="metric-label">
                  <span>TOTAL CASH</span>
                  <Tooltip title="Cash and liquid assets">
                    <IconInfoCircle size={14} />
                  </Tooltip>
                </div>
                <div className="metric-value">
                  {formatCurrency(metrics.totalCash)}
                </div>
              </div>
              <div className="metric-divider" />
              <div className="metric-item">
                <div className="metric-label">
                  <span>TOTAL ASSETS</span>
                  <Tooltip title="All assets including property">
                    <IconInfoCircle size={14} />
                  </Tooltip>
                </div>
                <div className="metric-value">
                  {formatCurrency(metrics.totalAssets)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Item Modal */}
      <AddItemModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdd={handleAddItems}
        existingItems={budgetItems.map((b) => b.name)}
      />
    </div>
  );
}

// Budget Item Row Component
function BudgetItemRow({
  item,
  type,
  onSave,
  onDelete,
  formatCurrency,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(item.value || 0);

  useEffect(() => {
    setEditValue(item.value || 0);
  }, [item.value]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      setIsEditing(false);
      setEditValue(item.value || 0);
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    if (editValue !== item.value) {
      onSave(editValue);
    }
  };

  return (
    <div className="budget-item-row">
      <div className="item-info">
        <div className={`item-indicator ${type}`} />
        <span className="item-name">{item.name}</span>
      </div>
      <div className="item-value-section">
        {isEditing ? (
          <div className="edit-input-wrapper">
            <span className="currency-prefix">$</span>
            <input
              type="number"
              className="edit-input"
              value={editValue}
              onChange={(e) => setEditValue(parseFloat(e.target.value) || 0)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              autoFocus
            />
          </div>
        ) : (
          <div 
            className="item-value-display clickable"
            onClick={() => setIsEditing(true)}
          >
            {formatCurrency(item.value || 0)}
          </div>
        )}
      </div>
      <div className="item-actions">
        <button className="action-btn delete" onClick={onDelete}>
          <IconTrash size={16} />
        </button>
      </div>
    </div>
  );
}

export default BudgetCalculator;
